import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  AlertTriangle, Package, CheckCircle, XCircle, DollarSign, Clock,
  RefreshCw, Loader2, Search, Filter, ChevronDown, Eye, Calendar,
  FileText, User, Warehouse, RotateCcw, CheckCircle2, Ban, TrendingUp,
  Download, BarChart3
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import { useSettings } from '@/context/SettingsContext';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// Status configuration
const statusConfig = {
  pending: { label: 'Pending', color: 'text-amber-400', bg: 'bg-amber-500/10', border: 'border-amber-500/30', icon: Clock },
  received: { label: 'Received', color: 'text-green-400', bg: 'bg-green-500/10', border: 'border-green-500/30', icon: CheckCircle2 },
  written_off: { label: 'Written Off', color: 'text-red-400', bg: 'bg-red-500/10', border: 'border-red-500/30', icon: Ban },
  claim_received: { label: 'Claim Received', color: 'text-blue-400', bg: 'bg-blue-500/10', border: 'border-blue-500/30', icon: DollarSign }
};

const MissingInventoryPage = () => {
  const { user } = useAuth();
  const { formatCurrency } = useSettings();
  
  const [claims, setClaims] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [statusCounts, setStatusCounts] = useState({});
  
  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [dateFilter, setDateFilter] = useState('all');
  
  // Pagination
  const [page, setPage] = useState(1);
  const [totalClaims, setTotalClaims] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const PAGE_SIZE = 50;
  
  // Selection for bulk actions
  const [selectedClaims, setSelectedClaims] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  
  // Detail dialog
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [selectedClaim, setSelectedClaim] = useState(null);
  
  // Resolve dialog
  const [resolveDialogOpen, setResolveDialogOpen] = useState(false);
  const [resolveStatus, setResolveStatus] = useState('received');
  const [resolveNotes, setResolveNotes] = useState('');
  const [resolving, setResolving] = useState(false);
  
  // Bulk resolve
  const [bulkResolveDialogOpen, setBulkResolveDialogOpen] = useState(false);
  
  const fetchClaims = useCallback(async (isRefresh = false) => {
    if (isRefresh) setRefreshing(true);
    else setLoading(true);
    
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      
      if (statusFilter !== 'all') {
        params.append('status', statusFilter);
      }
      params.append('page', page.toString());
      params.append('limit', PAGE_SIZE.toString());
      
      const response = await axios.get(`${API_BASE_URL}/v1/returns/missing-inventory?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      let fetchedClaims = response.data.claims || [];
      
      // Client-side search filter
      if (searchTerm) {
        const search = searchTerm.toLowerCase();
        fetchedClaims = fetchedClaims.filter(c => 
          c.sku?.toLowerCase().includes(search) ||
          c.product_name?.toLowerCase().includes(search) ||
          c.return_number?.toLowerCase().includes(search) ||
          c.manifest_number?.toLowerCase().includes(search)
        );
      }
      
      // Client-side date filter
      if (dateFilter !== 'all') {
        const now = new Date();
        const filterDate = new Date();
        
        switch (dateFilter) {
          case 'today':
            filterDate.setHours(0, 0, 0, 0);
            break;
          case 'week':
            filterDate.setDate(filterDate.getDate() - 7);
            break;
          case 'month':
            filterDate.setMonth(filterDate.getMonth() - 1);
            break;
          default:
            break;
        }
        
        fetchedClaims = fetchedClaims.filter(c => new Date(c.created_at) >= filterDate);
      }
      
      setClaims(fetchedClaims);
      setTotalClaims(response.data.total || fetchedClaims.length);
      setStatusCounts(response.data.status_counts || {});
      setHasMore(response.data.claims?.length === PAGE_SIZE);
    } catch (error) {
      console.error('Error fetching claims:', error);
      toast.error('Failed to load missing inventory claims');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [statusFilter, searchTerm, dateFilter, page]);
  
  useEffect(() => {
    fetchClaims();
  }, [fetchClaims]);
  
  // Handle selection
  const toggleClaimSelection = (claimId) => {
    setSelectedClaims(prev => {
      if (prev.includes(claimId)) {
        return prev.filter(id => id !== claimId);
      } else {
        return [...prev, claimId];
      }
    });
  };
  
  const toggleSelectAll = () => {
    if (selectAll) {
      setSelectedClaims([]);
    } else {
      setSelectedClaims(claims.filter(c => c.status === 'pending').map(c => c.id));
    }
    setSelectAll(!selectAll);
  };
  
  // View claim detail
  const handleViewClaim = (claim) => {
    setSelectedClaim(claim);
    setDetailDialogOpen(true);
  };
  
  // Open resolve dialog for single claim
  const handleResolveSingle = (claim) => {
    setSelectedClaim(claim);
    setSelectedClaims([claim.id]);
    setResolveStatus('received');
    setResolveNotes('');
    setResolveDialogOpen(true);
  };
  
  // Open bulk resolve dialog
  const handleBulkResolve = () => {
    if (selectedClaims.length === 0) {
      toast.error('Please select claims to resolve');
      return;
    }
    setResolveStatus('received');
    setResolveNotes('');
    setBulkResolveDialogOpen(true);
  };
  
  // Resolve claims
  const handleResolve = async () => {
    if (selectedClaims.length === 0) return;
    
    setResolving(true);
    try {
      const token = localStorage.getItem('token');
      let successCount = 0;
      let errorCount = 0;
      
      for (const claimId of selectedClaims) {
        try {
          await axios.post(
            `${API_BASE_URL}/v1/returns/missing-inventory/${claimId}/resolve?status=${resolveStatus}&notes=${encodeURIComponent(resolveNotes || '')}`,
            {},
            { headers: { Authorization: `Bearer ${token}` } }
          );
          successCount++;
        } catch (error) {
          errorCount++;
        }
      }
      
      if (successCount > 0) {
        toast.success(`${successCount} claim(s) resolved successfully`);
      }
      if (errorCount > 0) {
        toast.error(`${errorCount} claim(s) failed to resolve`);
      }
      
      setResolveDialogOpen(false);
      setBulkResolveDialogOpen(false);
      setSelectedClaims([]);
      setSelectAll(false);
      fetchClaims(true);
    } catch (error) {
      toast.error('Failed to resolve claims');
    } finally {
      setResolving(false);
    }
  };
  
  // Quick resolve (direct button click)
  const handleQuickResolve = async (claimId, status) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API_BASE_URL}/v1/returns/missing-inventory/${claimId}/resolve?status=${status}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Claim marked as ${status.replace('_', ' ')}`);
      fetchClaims(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to resolve claim');
    }
  };
  
  // Format date
  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };
  
  // Calculate summary stats
  const totalPendingValue = claims
    .filter(c => c.status === 'pending')
    .reduce((sum, c) => sum + (c.quantity * (c.unit_price || 0)), 0);
  
  return (
    <div className="min-h-screen bg-[#0a0a0f] text-white">
      {/* Header */}
      <div className="sticky top-0 z-30 bg-[#0a0a0f]/95 backdrop-blur-xl border-b border-white/5">
        <div className="px-4 py-4">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-xl font-semibold text-white flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-amber-400" />
                Missing Inventory Claims
              </h1>
              <p className="text-sm text-zinc-500 mt-0.5">
                Track and resolve missing items from returns
              </p>
            </div>
            <div className="flex items-center gap-2">
              {selectedClaims.length > 0 && (
                <Button
                  onClick={handleBulkResolve}
                  className="bg-green-600 hover:bg-green-700"
                  size="sm"
                >
                  <CheckCircle className="w-4 h-4 mr-1" />
                  Resolve ({selectedClaims.length})
                </Button>
              )}
              <Button
                onClick={() => fetchClaims(true)}
                variant="outline"
                size="sm"
                className="border-white/10 bg-white/5 hover:bg-white/10"
                disabled={refreshing}
              >
                <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
              </Button>
            </div>
          </div>
          
          {/* Stats Cards */}
          <div className="grid grid-cols-4 gap-3 mb-4">
            <div className="bg-amber-500/10 border border-amber-500/20 rounded-xl p-3">
              <div className="flex items-center gap-2 text-amber-400 mb-1">
                <Clock className="w-4 h-4" />
                <span className="text-xs">Pending</span>
              </div>
              <p className="text-xl font-semibold text-white">{statusCounts.pending || 0}</p>
            </div>
            <div className="bg-green-500/10 border border-green-500/20 rounded-xl p-3">
              <div className="flex items-center gap-2 text-green-400 mb-1">
                <CheckCircle2 className="w-4 h-4" />
                <span className="text-xs">Received</span>
              </div>
              <p className="text-xl font-semibold text-white">{statusCounts.received || 0}</p>
            </div>
            <div className="bg-red-500/10 border border-red-500/20 rounded-xl p-3">
              <div className="flex items-center gap-2 text-red-400 mb-1">
                <Ban className="w-4 h-4" />
                <span className="text-xs">Written Off</span>
              </div>
              <p className="text-xl font-semibold text-white">{statusCounts.written_off || 0}</p>
            </div>
            <div className="bg-blue-500/10 border border-blue-500/20 rounded-xl p-3">
              <div className="flex items-center gap-2 text-blue-400 mb-1">
                <DollarSign className="w-4 h-4" />
                <span className="text-xs">Claim Received</span>
              </div>
              <p className="text-xl font-semibold text-white">{statusCounts.claim_received || 0}</p>
            </div>
          </div>
          
          {/* Filters */}
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
              <Input
                placeholder="Search by SKU, product, return #..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 bg-white/5 border-white/10 text-white placeholder:text-zinc-500"
                data-testid="missing-search-input"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[150px] bg-white/5 border-white/10 text-white">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent className="bg-zinc-900 border-white/10">
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="received">Received</SelectItem>
                <SelectItem value="written_off">Written Off</SelectItem>
                <SelectItem value="claim_received">Claim Received</SelectItem>
              </SelectContent>
            </Select>
            <Select value={dateFilter} onValueChange={setDateFilter}>
              <SelectTrigger className="w-[150px] bg-white/5 border-white/10 text-white">
                <SelectValue placeholder="Date" />
              </SelectTrigger>
              <SelectContent className="bg-zinc-900 border-white/10">
                <SelectItem value="all">All Time</SelectItem>
                <SelectItem value="today">Today</SelectItem>
                <SelectItem value="week">Last 7 Days</SelectItem>
                <SelectItem value="month">Last 30 Days</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>
      
      {/* Claims List */}
      <div className="p-4">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-amber-400" />
          </div>
        ) : claims.length === 0 ? (
          <div className="text-center py-12 bg-white/[0.02] rounded-xl border border-white/5">
            <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-3" />
            <p className="text-zinc-400">No missing inventory claims</p>
            <p className="text-sm text-zinc-600 mt-1">
              {statusFilter !== 'all' ? 'Try changing your filters' : 'All returned items are accounted for'}
            </p>
          </div>
        ) : (
          <>
            {/* Select All Header */}
            {claims.some(c => c.status === 'pending') && (
              <div className="flex items-center gap-3 mb-4 p-3 bg-white/[0.02] rounded-lg border border-white/5">
                <Checkbox
                  checked={selectAll}
                  onCheckedChange={toggleSelectAll}
                />
                <span className="text-sm text-zinc-400">
                  Select all pending claims ({claims.filter(c => c.status === 'pending').length})
                </span>
              </div>
            )}
            
            {/* Claims Grid */}
            <div className="space-y-3">
              <AnimatePresence mode="popLayout">
                {claims.map((claim, index) => {
                  const status = statusConfig[claim.status] || statusConfig.pending;
                  const StatusIcon = status.icon;
                  const isSelected = selectedClaims.includes(claim.id);
                  const itemValue = claim.quantity * (claim.unit_price || 0);
                  
                  return (
                    <motion.div
                      key={claim.id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, scale: 0.95 }}
                      transition={{ delay: index * 0.03 }}
                      className={`bg-white/[0.02] border rounded-xl p-4 transition-all ${
                        isSelected ? 'border-amber-500/50 bg-amber-500/5' : 'border-white/5 hover:border-white/10'
                      }`}
                      data-testid={`claim-card-${claim.id}`}
                    >
                      <div className="flex items-start gap-3">
                        {claim.status === 'pending' && (
                          <Checkbox
                            checked={isSelected}
                            onCheckedChange={() => toggleClaimSelection(claim.id)}
                            className="mt-1"
                          />
                        )}
                        
                        <div className="flex-1">
                          <div className="flex items-start justify-between mb-3">
                            <div>
                              <div className="flex items-center gap-2 mb-1">
                                <span className="font-medium text-white">{claim.product_name}</span>
                                <Badge className={`${status.bg} ${status.color} border ${status.border} text-xs`}>
                                  <StatusIcon className="w-3 h-3 mr-1" />
                                  {status.label}
                                </Badge>
                              </div>
                              <p className="text-xs text-zinc-500 font-mono">SKU: {claim.sku}</p>
                            </div>
                            <div className="text-right">
                              <p className="text-lg font-semibold text-white">{claim.quantity}</p>
                              <p className="text-xs text-zinc-500">units</p>
                            </div>
                          </div>
                          
                          <div className="grid grid-cols-3 gap-4 text-sm mb-3">
                            <div>
                              <p className="text-xs text-zinc-500">Return</p>
                              <p className="text-zinc-300 font-mono">{claim.return_number}</p>
                            </div>
                            <div>
                              <p className="text-xs text-zinc-500">Manifest</p>
                              <p className="text-zinc-300 font-mono">{claim.manifest_number || '-'}</p>
                            </div>
                            <div>
                              <p className="text-xs text-zinc-500">Value</p>
                              <p className="text-zinc-300">{formatCurrency(itemValue)}</p>
                            </div>
                          </div>
                          
                          <div className="flex items-center justify-between">
                            <p className="text-xs text-zinc-600">
                              Reported: {formatDate(claim.created_at)}
                              {claim.resolved_at && ` • Resolved: ${formatDate(claim.resolved_at)}`}
                            </p>
                            
                            {claim.status === 'pending' && (
                              <div className="flex gap-2">
                                <Button
                                  onClick={() => handleQuickResolve(claim.id, 'received')}
                                  size="sm"
                                  className="bg-green-600 hover:bg-green-700 h-7 px-2"
                                >
                                  <CheckCircle2 className="w-3 h-3 mr-1" />
                                  Received
                                </Button>
                                <Button
                                  onClick={() => handleQuickResolve(claim.id, 'written_off')}
                                  size="sm"
                                  variant="outline"
                                  className="border-red-500/30 text-red-400 hover:bg-red-500/10 h-7 px-2"
                                >
                                  <Ban className="w-3 h-3 mr-1" />
                                  Write Off
                                </Button>
                                <Button
                                  onClick={() => handleQuickResolve(claim.id, 'claim_received')}
                                  size="sm"
                                  variant="outline"
                                  className="border-blue-500/30 text-blue-400 hover:bg-blue-500/10 h-7 px-2"
                                >
                                  <DollarSign className="w-3 h-3 mr-1" />
                                  Claimed
                                </Button>
                              </div>
                            )}
                            
                            {claim.status !== 'pending' && claim.resolution_notes && (
                              <Button
                                onClick={() => handleViewClaim(claim)}
                                variant="ghost"
                                size="sm"
                                className="text-zinc-400 hover:text-white h-7"
                              >
                                <Eye className="w-3 h-3 mr-1" />
                                View Details
                              </Button>
                            )}
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  );
                })}
              </AnimatePresence>
            </div>
            
            {/* Load More */}
            {hasMore && claims.length > 0 && (
              <div className="text-center pt-4">
                <Button
                  variant="outline"
                  onClick={() => setPage(p => p + 1)}
                  className="border-white/10 bg-white/5 hover:bg-white/10"
                >
                  Load More
                </Button>
              </div>
            )}
          </>
        )}
      </div>
      
      {/* Detail Dialog */}
      <Dialog open={detailDialogOpen} onOpenChange={setDetailDialogOpen}>
        <DialogContent className="bg-zinc-900 border-white/10 text-white">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Package className="w-5 h-5 text-amber-400" />
              Claim Details
            </DialogTitle>
          </DialogHeader>
          
          {selectedClaim && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-zinc-500">Product</Label>
                  <p className="text-white">{selectedClaim.product_name}</p>
                </div>
                <div>
                  <Label className="text-zinc-500">SKU</Label>
                  <p className="text-white font-mono">{selectedClaim.sku}</p>
                </div>
                <div>
                  <Label className="text-zinc-500">Quantity</Label>
                  <p className="text-white">{selectedClaim.quantity}</p>
                </div>
                <div>
                  <Label className="text-zinc-500">Status</Label>
                  <Badge className={`${statusConfig[selectedClaim.status]?.bg} ${statusConfig[selectedClaim.status]?.color}`}>
                    {statusConfig[selectedClaim.status]?.label}
                  </Badge>
                </div>
                <div>
                  <Label className="text-zinc-500">Return Number</Label>
                  <p className="text-white font-mono">{selectedClaim.return_number}</p>
                </div>
                <div>
                  <Label className="text-zinc-500">Manifest</Label>
                  <p className="text-white font-mono">{selectedClaim.manifest_number || '-'}</p>
                </div>
              </div>
              
              <div>
                <Label className="text-zinc-500">Reported</Label>
                <p className="text-white">{formatDate(selectedClaim.created_at)}</p>
              </div>
              
              {selectedClaim.resolved_at && (
                <>
                  <div>
                    <Label className="text-zinc-500">Resolved</Label>
                    <p className="text-white">{formatDate(selectedClaim.resolved_at)}</p>
                  </div>
                  {selectedClaim.resolution_notes && (
                    <div>
                      <Label className="text-zinc-500">Resolution Notes</Label>
                      <p className="text-white bg-white/5 p-3 rounded-lg mt-1">{selectedClaim.resolution_notes}</p>
                    </div>
                  )}
                </>
              )}
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setDetailDialogOpen(false)} className="border-white/10">
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Bulk Resolve Dialog */}
      <Dialog open={bulkResolveDialogOpen} onOpenChange={setBulkResolveDialogOpen}>
        <DialogContent className="bg-zinc-900 border-white/10 text-white">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-green-400" />
              Resolve {selectedClaims.length} Claims
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label>Resolution Status</Label>
              <Select value={resolveStatus} onValueChange={setResolveStatus}>
                <SelectTrigger className="bg-white/5 border-white/10 mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-zinc-900 border-white/10">
                  <SelectItem value="received">
                    <div className="flex items-center gap-2">
                      <CheckCircle2 className="w-4 h-4 text-green-400" />
                      Received - Items found/returned
                    </div>
                  </SelectItem>
                  <SelectItem value="written_off">
                    <div className="flex items-center gap-2">
                      <Ban className="w-4 h-4 text-red-400" />
                      Written Off - Items lost permanently
                    </div>
                  </SelectItem>
                  <SelectItem value="claim_received">
                    <div className="flex items-center gap-2">
                      <DollarSign className="w-4 h-4 text-blue-400" />
                      Claim Received - Insurance/courier claim paid
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label>Notes (optional)</Label>
              <Textarea
                value={resolveNotes}
                onChange={(e) => setResolveNotes(e.target.value)}
                className="bg-white/5 border-white/10 mt-1"
                placeholder="Add resolution notes..."
                rows={3}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setBulkResolveDialogOpen(false)} className="border-white/10">
              Cancel
            </Button>
            <Button onClick={handleResolve} disabled={resolving} className="bg-green-600 hover:bg-green-700">
              {resolving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Resolve Claims
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MissingInventoryPage;
